import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const Login = () => {
  useSEO({
    title: 'Sign In',
    description: 'Sign in to your Family Wishlist account to manage your family wishlist, view family member lists, and coordinate gifts safely without spoiling surprises.',
    keywords: 'family wishlist login, sign in family wishlist, gift list sharing, family gift list',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'Sign In - Family Wishlist',
      'description': 'Authentication portal to sign into your Family Wishlist account.',
      'url': `${window.location.origin}/login`
    }
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.token) {
        navigate('/');
      }
    } catch (err) {
      if (err.response?.data?.unverified) {
        navigate('/verify-email', { state: { email: err.response.data.email } });
      } else {
        setError(err.response?.data?.msg || 'Invalid email or password');
        setLoading(false);
      }
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 card">
      <h2 className="text-3xl font-bold text-center text-xmas-red mb-6">Welcome!</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium">Email</label>
          <input
            type="email"
            className="w-full p-2 border rounded mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium">Password</label>
            <Link to="/forgot-password" className="text-xs text-xmas-green font-medium hover:underline">
              Forgot Password?
            </Link>
          </div>
          <div className="relative mt-1">
            <input
              type={showPassword ? 'text' : 'password'}
              className="w-full p-2 border rounded pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-2 top-2 text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded relative text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn-primary w-full disabled:opacity-50"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Signing In...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>
      <p className="mt-4 text-center">
        Don't have an account? <Link to="/register" className="text-xmas-green font-bold">Register</Link>
      </p>
    </div>
  );
};

export default Login;
